import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Target, Check, PencilLine, X, AlertTriangle, Clock, Scale, DollarSign, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { IntakeResumeBanner } from "@/components/IntakeResumeBanner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const RESULTS = [
  ["completed", "Completed"],
  ["partial", "Partial"],
  ["blocked", "Blocked"],
  ["changed", "Changed"],
  ["not_addressed", "Not addressed"],
];

function Chip({ children, tone = "muted", testid }) {
  const tones = {
    muted: "border-wr-border text-wr-muted",
    red: "border-wr-red/40 text-wr-red",
    amber: "border-amber-500/40 text-amber-400",
    green: "border-emerald-500/40 text-emerald-400",
  };
  return <span data-testid={testid} className={`wr-mono text-[0.6rem] uppercase px-2 py-1 border ${tones[tone]}`}>{children}</span>;
}

export default function Today() {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [text, setText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coo/today");
      setCycle(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const respond = async (status, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/coo/cycles/${cycle.id}/respond`, { status, ...extra });
      setCycle(data);
      setModifying(false); setRejecting(false); setText("");
      toast.success(status === "accept" ? "Move accepted — commitment created." : status === "modify" ? "Modified move committed." : "Recorded.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setResult = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/coo/cycles/${cycle.id}/result`, { status, note: text });
      setCycle(data); setText("");
      toast.success("Outcome recorded.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 wr-label" data-testid="today-loading">Preparing today's command cycle…</div>;
  if (!cycle) return <div className="p-8 text-wr-sub" data-testid="today-empty">No cycle available.</div>;

  const resp = cycle.founder_response?.status || "pending";
  const fin = cycle.financial || {};
  const fm = fin.metrics || {};

  return (
    <div className="flex flex-col min-h-full">
      <IntakeResumeBanner />
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-8 py-6 sm:py-8 pb-28" data-testid="today-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="wr-label !text-wr-red">▸ SARGE · TODAY</p>
            <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl mt-2" data-testid="today-date">
              Command Cycle · {cycle.cycle_date}
            </h1>
          </div>
          {cycle.confidence && <Chip tone={cycle.confidence === "high" ? "green" : cycle.confidence === "low" ? "amber" : "muted"} testid="today-confidence">confidence: {cycle.confidence}</Chip>}
        </div>

        {/* Situation */}
        {cycle.situation && (
          <div className="mt-5 border-l-2 border-wr-border pl-4" data-testid="today-situation">
            <p className="wr-label">Situation</p>
            <p className="text-sm text-wr-sub mt-1 leading-relaxed">{cycle.situation}</p>
          </div>
        )}

        {/* Recommended move */}
        <div className="mt-6 border border-wr-red/30 bg-wr-redMuted p-5" data-testid="today-move">
          <p className="wr-label !text-wr-red flex items-center gap-2"><Target size={13} /> Recommended next move</p>
          {cycle.recommendation_error ? (
            <p className="text-sm text-amber-400 mt-2" data-testid="today-move-error">{cycle.recommendation_error}</p>
          ) : (
            <>
              <p className="text-base text-white mt-2 leading-relaxed" data-testid="today-move-text">{cycle.recommended_move}</p>
              {cycle.rationale && <p className="text-xs text-wr-muted mt-2" data-testid="today-rationale">Why: {cycle.rationale}</p>}
            </>
          )}
          {cycle.missing_critical?.length > 0 && (
            <div className="mt-3 border border-amber-500/30 bg-amber-500/5 p-3" data-testid="today-missing">
              <p className="wr-label !text-amber-400 flex items-center gap-2"><HelpCircle size={12} /> Sarge needs to know</p>
              <ul className="mt-1 list-disc list-inside text-xs text-amber-300/90">
                {cycle.missing_critical.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
              <button onClick={() => navigate("/app/sarge")} data-testid="today-answer-sarge" className="wr-label hover:text-white mt-2">Answer in Sarge →</button>
            </div>
          )}

          {/* Decision actions */}
          {resp === "pending" && !cycle.recommendation_error && (
            <div className="mt-5" data-testid="today-actions">
              {!modifying && !rejecting && (
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => respond("accept")} disabled={busy} data-testid="today-accept" className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-10 text-xs">
                    <Check size={14} className="mr-1.5" /> Accept
                  </Button>
                  <Button onClick={() => setModifying(true)} variant="outline" data-testid="today-modify-open" className="rounded-none border-wr-border bg-transparent text-wr-sub hover:text-white wr-mono uppercase tracking-wider h-10 text-xs">
                    <PencilLine size={14} className="mr-1.5" /> Modify
                  </Button>
                  <Button onClick={() => setRejecting(true)} variant="outline" data-testid="today-reject-open" className="rounded-none border-wr-border bg-transparent text-wr-sub hover:text-white wr-mono uppercase tracking-wider h-10 text-xs">
                    <X size={14} className="mr-1.5" /> Reject
                  </Button>
                </div>
              )}
              {modifying && (
                <div data-testid="today-modify-form">
                  <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Your version of the move…" data-testid="today-modify-text" className="bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
                  <div className="flex gap-2 mt-2">
                    <Button onClick={() => respond("modify", { modified_move: text })} disabled={busy || !text.trim()} data-testid="today-modify-submit" className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase text-xs h-9">Commit modified</Button>
                    <Button onClick={() => setModifying(false)} variant="outline" className="rounded-none border-wr-border bg-transparent text-wr-sub wr-mono uppercase text-xs h-9">Cancel</Button>
                  </div>
                </div>
              )}
              {rejecting && (
                <div data-testid="today-reject-form">
                  <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Why are you rejecting this move? (optional — Sarge learns from it)" data-testid="today-reject-text" className="bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
                  <div className="flex gap-2 mt-2">
                    <Button onClick={() => respond("reject", { reason: text })} disabled={busy} data-testid="today-reject-submit" className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase text-xs h-9">Reject move</Button>
                    <Button onClick={() => setRejecting(false)} variant="outline" className="rounded-none border-wr-border bg-transparent text-wr-sub wr-mono uppercase text-xs h-9">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {resp !== "pending" && (
            <div className="mt-4" data-testid="today-response-state">
              <Chip tone={resp === "rejected" ? "amber" : "green"} testid="today-response-chip">{resp}{cycle.linked_commitment_ids?.length ? " · commitment created" : ""}</Chip>
              {cycle.founder_response?.reason && <p className="text-xs text-wr-muted mt-2">Note: {cycle.founder_response.reason}</p>}
              {/* End-of-day result */}
              {!cycle.result?.status ? (
                <div className="mt-4" data-testid="today-result-actions">
                  <p className="wr-label">End-of-day result</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {RESULTS.map(([s, label]) => (
                      <button key={s} onClick={() => setResult(s)} disabled={busy} data-testid={`today-result-${s}`} className="wr-mono text-[0.6rem] uppercase px-2.5 py-1.5 border border-wr-border text-wr-sub hover:text-white hover:border-wr-red">{label}</button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-4 border-l-2 border-emerald-500/40 pl-3" data-testid="today-result-shown">
                  <Chip tone="green">{cycle.result.status}</Chip>
                  <p className="text-xs text-wr-sub mt-1.5">{cycle.result.sarge_assessment}</p>
                  {cycle.result.note && <p className="text-xs text-wr-muted mt-1">Note: {cycle.result.note}</p>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Context grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-wr-border border border-wr-border">
          <div className="bg-wr-surface p-4" data-testid="today-priorities">
            <p className="wr-label flex items-center gap-2"><Target size={12} className="text-wr-red" /> Supporting priorities</p>
            {cycle.supporting_priorities?.length ? (
              <ul className="mt-2 space-y-1">{cycle.supporting_priorities.map((p) => <li key={p.id} className="text-sm text-wr-sub">• {p.title}</li>)}</ul>
            ) : <p className="text-xs text-wr-muted mt-2">None recorded.</p>}
          </div>
          <div className="bg-wr-surface p-4" data-testid="today-financial">
            <p className="wr-label flex items-center gap-2"><DollarSign size={12} className="text-wr-red" /> Financial pulse</p>
            <p className="text-sm text-wr-sub mt-2">{fin.summary}</p>
            {fin.flags?.stale && <Chip tone="amber" testid="today-fin-stale">stale data</Chip>}
            <button onClick={() => navigate("/app/financial")} data-testid="today-open-financial" className="wr-label hover:text-white block mt-2">Update financials →</button>
          </div>
          <div className="bg-wr-surface p-4" data-testid="today-overdue">
            <p className="wr-label flex items-center gap-2"><Clock size={12} className="text-wr-red" /> Overdue commitments ({cycle.overdue_commitments?.length || 0})</p>
            {cycle.overdue_commitments?.length ? (
              <ul className="mt-2 space-y-1">{cycle.overdue_commitments.map((c) => <li key={c.id} className="text-sm text-amber-400">• {c.description}</li>)}</ul>
            ) : <p className="text-xs text-wr-muted mt-2">Nothing overdue.</p>}
          </div>
          <div className="bg-wr-surface p-4" data-testid="today-decisions-due">
            <p className="wr-label flex items-center gap-2"><Scale size={12} className="text-wr-red" /> Decisions due for review ({cycle.decisions_due?.length || 0})</p>
            {cycle.decisions_due?.length ? (
              <ul className="mt-2 space-y-1">{cycle.decisions_due.map((d) => <li key={d.id} className="text-sm text-wr-sub">• {d.title}</li>)}</ul>
            ) : <p className="text-xs text-wr-muted mt-2">None due.</p>}
            <button onClick={() => navigate("/app/decisions")} data-testid="today-open-decisions" className="wr-label hover:text-white block mt-2">Decision journal →</button>
          </div>
        </div>
        {cycle.active_risks?.length > 0 && (
          <div className="mt-4 border border-wr-border bg-wr-surface p-4" data-testid="today-risks">
            <p className="wr-label flex items-center gap-2"><AlertTriangle size={12} className="text-wr-red" /> Active risks</p>
            <ul className="mt-2 space-y-1">{cycle.active_risks.map((r) => <li key={r.id} className="text-sm text-wr-sub">• {r.title} {r.severity ? <span className="text-wr-muted">({r.severity})</span> : null}</li>)}</ul>
          </div>
        )}
      </div>
    </div>
  );
}
